// What the teacher chose to display to the whole class for a question:
//  - answers : every submitted answer (optionally anonymous + shuffled)
//  - correct : the correct answer, spelled out (or circled, for hotspot)
//  - stats   : how the class did (per-option counts or score buckets)
// KEEP IN SYNC with the dashboard original.

const BAR_COLORS = ["#0080dc", "#43a047", "#fb8c00", "#8e24aa", "#e53935", "#00897b", "#f6bf26", "#5c6bc0"];

const RevealOverlay = ({
    reveal,
    isTeacher,
    onClose,
    anonLabel,
    labels = {},
    isPrivate = false,      // teacher-only preview: nothing left this machine
    isBroadcasting = false, // this exact view is currently on student screens
    onBroadcast,
    onStopBroadcast,
}) => {
    if (!reveal) return null;
    const mode = reveal.mode || "answers";

    const icon = mode === "correct" ? "✅" : mode === "stats" ? "📊" : reveal.anonymous ? "🕶" : "👁";

    return (
        <div
            dir="rtl"
            style={{
                position: "absolute",
                inset: 12,
                zIndex: 7,
                background: "rgba(255,255,255,0.97)",
                borderRadius: 14,
                boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
                border: isPrivate ? "3px dashed #955e0a" : isBroadcasting ? "3px solid #d32f2f" : "none",
                padding: "16px 20px",
                overflowY: "auto",
                color: "#1b2430",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                <strong style={{ flex: 1, fontSize: "1.05rem", minWidth: 140 }}>{icon} {reveal.question}</strong>

                {/* who can see this right now */}
                {isTeacher && (
                    isBroadcasting ? (
                        <span style={{ fontSize: "0.78rem", background: "#fbe5e5", color: "#a83a3a", borderRadius: 999, padding: "3px 12px", fontWeight: 800 }}>
                            🔴 {labels.shownToStudents}
                        </span>
                    ) : (
                        <span style={{ fontSize: "0.78rem", background: "#fdf0d7", color: "#7a4c06", borderRadius: 999, padding: "3px 12px", fontWeight: 800 }}>
                            🔒 {labels.privateOnly}
                        </span>
                    )
                )}

                {mode === "answers" && reveal.anonymous && (
                    <span style={{ fontSize: "0.75rem", background: "#efe6fa", color: "#6b3fa0", borderRadius: 10, padding: "2px 10px", fontWeight: 700 }}>
                        {anonLabel}
                    </span>
                )}

                {isTeacher && (isBroadcasting ? (
                    <button
                        type="button"
                        onClick={onStopBroadcast}
                        style={{ border: "none", background: "#d32f2f", color: "#fff", borderRadius: 10, padding: "7px 16px", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                    >
                        🚫 {labels.stopBroadcast}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={onBroadcast}
                        style={{ border: "none", background: "#0080dc", color: "#fff", borderRadius: 10, padding: "7px 16px", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                    >
                        📢 {labels.broadcast}
                    </button>
                ))}

                {isTeacher && (
                    <button
                        type="button"
                        onClick={onClose}
                        style={{ border: "none", background: "#eef2f7", color: "#44566a", borderRadius: 8, width: 30, height: 30, cursor: "pointer" }}
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* ---- correct answer ---- */}
            {mode === "correct" && (
                <div style={{ textAlign: "center" }}>
                    {reveal.correctText && (
                        <div style={{
                            display: "inline-block",
                            background: "#e2f3e8",
                            border: "3px solid #1b7a3d",
                            color: "#12522a",
                            borderRadius: 16,
                            padding: "16px 30px",
                            fontSize: "1.5rem",
                            fontWeight: 800,
                            lineHeight: 1.6,
                            maxWidth: "100%",
                            wordBreak: "break-word",
                        }}>
                            {reveal.correctText}
                        </div>
                    )}
                    {reveal.type === "hotspot" && reveal.image && (
                        <div style={{ position: "relative", display: "inline-block", maxWidth: "min(100%, 620px)", borderRadius: 12, overflow: "hidden" }}>
                            <img src={reveal.image} alt="" style={{ width: "100%", display: "block" }} />
                            {reveal.solution && (
                                <span style={{
                                    position: "absolute",
                                    left: `${reveal.solution.x * 100}%`,
                                    top: `${reveal.solution.y * 100}%`,
                                    width: `${reveal.solution.r * 2 * 100}%`,
                                    aspectRatio: "1",
                                    transform: "translate(-50%, -50%)",
                                    border: "4px solid #1b7a3d",
                                    borderRadius: "50%",
                                    background: "rgba(27,122,61,0.18)",
                                }} />
                            )}
                        </div>
                    )}
                    {!reveal.correctText && reveal.type === "writing" && (
                        <div style={{ color: "#5b6b7a" }}>{labels.noFixedAnswer}</div>
                    )}

                    {/* why each option is right or wrong */}
                    {reveal.notes && (
                        <div style={{ display: "grid", gap: 8, marginTop: 18, textAlign: "start" }}>
                            {reveal.notes.map((note) => (
                                <div
                                    key={note.key}
                                    style={{
                                        background: note.is_correct ? "#e2f3e8" : "#f5f7fa",
                                        borderInlineStart: `4px solid ${note.is_correct ? "#1b7a3d" : "#b0bec5"}`,
                                        borderRadius: 10,
                                        padding: "10px 14px",
                                    }}
                                >
                                    <div style={{ fontWeight: 800, marginBottom: 3, color: note.is_correct ? "#12522a" : "#37474f" }}>
                                        {note.is_correct ? "✅" : "✖️"} {note.title}
                                    </div>
                                    {note.explanation && (
                                        <div style={{ fontSize: "0.9rem", color: "#4a5b6a", lineHeight: 1.6 }}>{note.explanation}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ---- stats ---- */}
            {mode === "stats" && reveal.stats && (
                <div>
                    <div style={{ fontWeight: 700, marginBottom: 12, color: "#5b6b7a" }}>
                        {labels.answeredOf
                            ? labels.answeredOf.replace("{{a}}", reveal.stats.answered).replace("{{b}}", reveal.stats.total)
                            : `${reveal.stats.answered} / ${reveal.stats.total}`}
                    </div>
                    {reveal.stats.options ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {reveal.stats.options.map((option, index) => {
                                const total = Math.max(reveal.stats.answered, 1);
                                const pct = Math.round((option.count / total) * 100);
                                return (
                                    <div key={option.key}>
                                        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginBottom: 3 }}>
                                            <span style={{ color: option.correct ? "#1b7a3d" : undefined }}>
                                                {option.correct ? "✓ " : ""}{option.title}
                                            </span>
                                            <span style={{ fontVariantNumeric: "tabular-nums" }}>{option.count} · {pct}%</span>
                                        </div>
                                        <div style={{ height: 16, borderRadius: 8, background: "rgba(0,0,0,0.08)", overflow: "hidden" }}>
                                            <div style={{
                                                height: "100%",
                                                width: `${pct}%`,
                                                minWidth: option.count > 0 ? 10 : 0,
                                                borderRadius: 8,
                                                background: option.correct ? "#1b7a3d" : BAR_COLORS[index % BAR_COLORS.length],
                                                transition: "width 400ms ease",
                                            }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            {[
                                { label: labels.fullMark, value: reveal.stats.full, color: "#e2f3e8", fg: "#1b7a3d" },
                                { label: labels.partialMark, value: reveal.stats.partial, color: "#fdf0d7", fg: "#955e0a" },
                                { label: labels.zeroMark, value: reveal.stats.zero, color: "#fbe5e5", fg: "#a83a3a" },
                                { label: labels.average, value: `${reveal.stats.average} / ${reveal.maxScore}`, color: "#e3f1fc", fg: "#0080dc" },
                            ].map((box) => (
                                <div key={box.label} style={{ flex: "1 1 130px", background: box.color, color: box.fg, borderRadius: 12, padding: "14px 18px", textAlign: "center" }}>
                                    <div style={{ fontSize: "1.7rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{box.value}</div>
                                    <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>{box.label}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ---- answers ---- */}
            {mode === "answers" && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
                    {reveal.items.map((item, index) => (
                        <div key={index} style={{ background: "#f2f6fa", borderRadius: 10, padding: "10px 14px" }}>
                            {item.name && (
                                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#0080dc", marginBottom: 4 }}>{item.name}</div>
                            )}
                            <div style={{ fontSize: "0.95rem", wordBreak: "break-word" }}>{String(item.answer)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RevealOverlay;
